import { useEffect, useRef, useState } from "react";
import type { Message } from "@cloudflare/realtimekit";
import {
  ChevronLeft,
  ChevronRight,
  Mic,
  MicOff,
  Monitor,
  MonitorOff,
  Send,
  Video,
  VideoOff,
  Circle,
  Square,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getNameInitials } from "./participantName";
import { getLivestreamChatText } from "./useLivestreamChat";
import type { StageStatus } from "@cloudflare/realtimekit";
import type { LivestreamStageRequest } from "./useLivestreamStageHost";

function LivestreamHostChatPanel({
  messages,
  onSendMessage,
}: {
  messages: Message[];
  onSendMessage: ({ text }: { text: string }) => Promise<void>;
}) {
  const chatInputRef = useRef<HTMLInputElement>(null);
  const refocusChatAfterSendRef = useRef(false);
  const [chatDraft, setChatDraft] = useState("");
  const [sendingChat, setSendingChat] = useState(false);

  useEffect(() => {
    if (!refocusChatAfterSendRef.current || sendingChat) return;
    refocusChatAfterSendRef.current = false;
    chatInputRef.current?.focus();
  }, [sendingChat]);

  const textMessages = messages
    .map((message) => ({ message, text: getLivestreamChatText(message) }))
    .filter((entry): entry is { message: Message; text: string } => entry.text != null);

  const handleSendChat = async () => {
    if (!chatDraft.trim() || sendingChat) return;
    setSendingChat(true);
    try {
      await onSendMessage({ text: chatDraft });
      setChatDraft("");
      refocusChatAfterSendRef.current = true;
    } finally {
      setSendingChat(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {textMessages.length === 0 ? (
          <p className="text-sm text-white/60">No messages yet.</p>
        ) : (
          textMessages.map(({ message, text }) => (
            <div key={message.id} className="flex gap-2.5 text-sm">
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-xs font-semibold text-white"
                aria-label={message.displayName}
              >
                {getNameInitials({ name: message.displayName })}
              </div>
              <div className="min-w-0 flex-1">
                <span className="font-medium text-white">{message.displayName}</span>
                <p className="mt-0.5 break-words text-white/70">{text}</p>
              </div>
            </div>
          ))
        )}
      </div>
      <form
        className="flex items-center gap-2 border-t border-white/10 px-3 py-3"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSendChat();
        }}
      >
        <Input
          ref={chatInputRef}
          value={chatDraft}
          onChange={(event) => setChatDraft(event.target.value)}
          placeholder="Say something…"
          disabled={sendingChat}
          className="h-9 flex-1 border-white/20 bg-white/5 text-white placeholder:text-white/50"
          aria-label="Live chat message"
        />
        <Button
          type="submit"
          size="icon"
          variant="outline"
          disabled={!chatDraft.trim() || sendingChat}
          className="h-9 w-9 shrink-0 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
          aria-label="Send message"
          onMouseDown={(event) => event.preventDefault()}
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}

function LivestreamHostParticipantsPanel({
  participants,
  stageRequests,
  onGrantStageAccess,
  onDenyStageAccess,
  onRemoveFromStage,
  actingOnUserId,
  canGrantStageAccess,
}: {
  participants: {
    id: string;
    userId: string;
    name: string;
    isSelf: boolean;
    stageStatus: StageStatus | undefined;
    stageLabel: string;
  }[];
  stageRequests: LivestreamStageRequest[];
  onGrantStageAccess: ({ userId }: { userId: string }) => Promise<void>;
  onDenyStageAccess: ({ userId }: { userId: string }) => Promise<void>;
  onRemoveFromStage: ({ userId }: { userId: string }) => Promise<void>;
  actingOnUserId: string | null;
  canGrantStageAccess: boolean;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="space-y-3 px-4 py-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-white/50">
            Join requests
          </p>
          {stageRequests.length === 0 ? (
            <p className="mt-2 text-sm text-white/60">No pending requests.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {stageRequests.map((request) => {
                const busy = actingOnUserId === request.userId;
                return (
                  <li
                    key={request.userId}
                    className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2"
                  >
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-xs font-semibold text-white"
                      aria-label={request.displayName}
                    >
                      {getNameInitials({ name: request.displayName })}
                    </div>
                    <span className="min-w-0 flex-1 truncate text-sm text-white">
                      {request.displayName}
                    </span>
                    <div className="flex shrink-0 gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!canGrantStageAccess || busy}
                        className="h-7 border-white/20 bg-white/5 px-2 text-xs text-white hover:bg-white/10 hover:text-white"
                        onClick={() => void onGrantStageAccess({ userId: request.userId })}
                      >
                        Accept
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        className="h-7 border-white/20 bg-white/5 px-2 text-xs text-white hover:bg-white/10 hover:text-white"
                        onClick={() => void onDenyStageAccess({ userId: request.userId })}
                      >
                        Deny
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-white/50">
            Participants
          </p>
          {participants.length === 0 ? (
            <p className="mt-2 text-sm text-white/60">No participants yet.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {participants.map((participant) => {
                const busy = actingOnUserId === participant.userId;
                const canRemoveFromStage =
                  !participant.isSelf && participant.stageStatus === "ON_STAGE";
                return (
                  <li
                    key={participant.id}
                    className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2"
                  >
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-xs font-semibold text-white"
                      aria-label={participant.name}
                    >
                      {getNameInitials({ name: participant.name })}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-white">
                        {participant.name}
                        {participant.isSelf ? (
                          <span className="ml-1 font-normal text-white/50">(you)</span>
                        ) : null}
                      </p>
                      <p className="text-xs text-white/50">{participant.stageLabel}</p>
                    </div>
                    {canRemoveFromStage ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        className="h-7 shrink-0 border-white/20 bg-white/5 px-2 text-xs text-white hover:bg-white/10 hover:text-white"
                        onClick={() => void onRemoveFromStage({ userId: participant.userId })}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function LivestreamHostSettingsPanel({
  audioEnabled,
  videoEnabled,
  screenShareEnabled,
  canShareScreen,
  canRecord,
  isRecordingActive,
  recordingBusy,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onToggleRecording,
}: {
  audioEnabled: boolean;
  videoEnabled: boolean;
  screenShareEnabled: boolean;
  canShareScreen: boolean;
  canRecord: boolean;
  isRecordingActive: boolean;
  recordingBusy: boolean;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  onToggleRecording: () => void;
}) {
  return (
    <div className="space-y-4 px-4 py-3">
      <p className="text-sm text-white/60">Stream controls</p>
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          className="justify-start gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
          onClick={onToggleAudio}
        >
          {audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
          {audioEnabled ? "Mute microphone" : "Unmute microphone"}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="justify-start gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
          onClick={onToggleVideo}
        >
          {videoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
          {videoEnabled ? "Turn off camera" : "Turn on camera"}
        </Button>
        {canShareScreen ? (
          <Button
            type="button"
            variant="outline"
            className="justify-start gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
            onClick={onToggleScreenShare}
          >
            {screenShareEnabled ? (
              <MonitorOff className="h-4 w-4" />
            ) : (
              <Monitor className="h-4 w-4" />
            )}
            {screenShareEnabled ? "Stop screen share" : "Share screen"}
          </Button>
        ) : null}
        {canRecord ? (
          <Button
            type="button"
            variant="outline"
            disabled={recordingBusy}
            className="justify-start gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
            onClick={onToggleRecording}
          >
            {isRecordingActive ? (
              <Square className="h-4 w-4" />
            ) : (
              <Circle className="h-4 w-4" />
            )}
            {isRecordingActive ? "Stop recording" : "Start recording"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function LivestreamHostSidebar({
  messages,
  onSendMessage,
  participants,
  stageRequests,
  onGrantStageAccess,
  onDenyStageAccess,
  onRemoveFromStage,
  actingOnUserId,
  canGrantStageAccess,
  audioEnabled,
  videoEnabled,
  screenShareEnabled,
  canShareScreen,
  canRecord,
  isRecordingActive,
  recordingBusy,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onToggleRecording,
}: {
  messages: Message[];
  onSendMessage: ({ text }: { text: string }) => Promise<void>;
  participants: {
    id: string;
    userId: string;
    name: string;
    isSelf: boolean;
    stageStatus: StageStatus | undefined;
    stageLabel: string;
  }[];
  stageRequests: LivestreamStageRequest[];
  onGrantStageAccess: ({ userId }: { userId: string }) => Promise<void>;
  onDenyStageAccess: ({ userId }: { userId: string }) => Promise<void>;
  onRemoveFromStage: ({ userId }: { userId: string }) => Promise<void>;
  actingOnUserId: string | null;
  canGrantStageAccess: boolean;
  audioEnabled: boolean;
  videoEnabled: boolean;
  screenShareEnabled: boolean;
  canShareScreen: boolean;
  canRecord: boolean;
  isRecordingActive: boolean;
  recordingBusy: boolean;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  onToggleRecording: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`relative flex h-full shrink-0 flex-col border-l border-white/10 bg-zinc-950 text-white transition-[width] duration-200 ease-linear ${
        collapsed ? "w-10" : "w-72 sm:w-80"
      }`}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setCollapsed((value) => !value)}
        className="absolute -left-3 top-3 z-10 h-6 w-6 rounded-full border border-white/10 bg-zinc-950 text-white/70 shadow-sm hover:bg-zinc-900 hover:text-white"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <ChevronLeft className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </Button>
      {collapsed ? null : (
        <Tabs defaultValue="chat" className="flex min-h-0 flex-1 flex-col gap-0">
          <TabsList className="mx-3 mt-3 grid w-auto grid-cols-3 bg-white/10">
            <TabsTrigger
              value="chat"
              className="text-xs text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white sm:text-sm"
            >
              Chat
            </TabsTrigger>
            <TabsTrigger
              value="people"
              className="text-xs text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white sm:text-sm"
            >
              People
              {stageRequests.length > 0 ? (
                <span className="ml-1 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] leading-none">
                  {stageRequests.length}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="text-xs text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white sm:text-sm"
            >
              Settings
            </TabsTrigger>
          </TabsList>
          <TabsContent value="chat" className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">
            <LivestreamHostChatPanel messages={messages} onSendMessage={onSendMessage} />
          </TabsContent>
          <TabsContent
            value="people"
            className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          >
            <LivestreamHostParticipantsPanel
              participants={participants}
              stageRequests={stageRequests}
              onGrantStageAccess={onGrantStageAccess}
              onDenyStageAccess={onDenyStageAccess}
              onRemoveFromStage={onRemoveFromStage}
              actingOnUserId={actingOnUserId}
              canGrantStageAccess={canGrantStageAccess}
            />
          </TabsContent>
          <TabsContent
            value="settings"
            className="mt-0 min-h-0 flex-1 overflow-y-auto data-[state=inactive]:hidden"
          >
            <LivestreamHostSettingsPanel
              audioEnabled={audioEnabled}
              videoEnabled={videoEnabled}
              screenShareEnabled={screenShareEnabled}
              canShareScreen={canShareScreen}
              canRecord={canRecord}
              isRecordingActive={isRecordingActive}
              recordingBusy={recordingBusy}
              onToggleAudio={onToggleAudio}
              onToggleVideo={onToggleVideo}
              onToggleScreenShare={onToggleScreenShare}
              onToggleRecording={onToggleRecording}
            />
          </TabsContent>
        </Tabs>
      )}
    </aside>
  );
}
